import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import GuideLead from '../components/GuideLead';

const GuideFormatCV = () => {
    return (
        <div className="min-h-screen bg-slate-200">
            <Helmet>
                <title>CV Chronologique vs Fonctionnel vs Mixte : Quel Format Choisir ? Guide 2025</title>
                <meta name="description" content="Comparaison détaillée des formats de CV : chronologique, fonctionnel et mixte. Découvrez quel format choisir selon votre profil, avantages/inconvénients, exemples et compatibilité ATS." />
                <meta name="keywords" content="format CV, CV chronologique, CV fonctionnel, CV mixte, type CV, structure CV, quel format CV choisir, CV antéchronologique" />
                <link rel="canonical" href="https://www.cv-analyse.fr/guide/format-cv" />
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "HowTo",
                  "name": "Comment choisir le bon format de CV",
                  "description": "Comparez les formats chronologique, fonctionnel et mixte pour choisir celui qui correspond le mieux à votre profil et votre secteur.",
                  "step": [
                    {"@type": "HowToStep", "name": "Évaluer son profil et son expérience", "text": "Analysez votre parcours : linéaire, avec des gaps, en reconversion ou débutant."},
                    {"@type": "HowToStep", "name": "Comparer les 3 formats", "text": "Chronologique : parcours linéaire. Fonctionnel : compétences avant expériences. Mixte : les deux."},
                    {"@type": "HowToStep", "name": "Choisir le format adapté à son secteur", "text": "Le chronologique est universellement accepté. Le fonctionnel convient mieux aux reconversions."},
                    {"@type": "HowToStep", "name": "Structurer les sections dans le bon ordre", "text": "Coordonnées, accroche, expériences, formation, compétences, langues, centres d'intérêt."},
                    {"@type": "HowToStep", "name": "Vérifier la compatibilité ATS", "text": "Évitez les tableaux et zones de texte qui perturbent les logiciels de tri automatique."}
                  ]
                }
                `}</script>
            </Helmet>

            <nav aria-label="Fil d'Ariane" className="pt-16 pb-4">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <ol className="flex items-center space-x-2 text-sm text-slate-600">
                        <li><Link to="/" className="hover:text-white transition-colors">Accueil</Link></li>
                        <li className="text-slate-400">/</li>
                        <li><Link to="/guides" className="hover:text-white transition-colors">Guides</Link></li>
                        <li className="text-slate-400">/</li>
                        <li className="text-white">Format CV</li>
                    </ol>
                </div>
            </nav>

            <div className="bg-slate-900 pt-4 pb-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-3xl mb-6">
                            <span className="text-4xl">📋</span>
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                            CV Chronologique vs Fonctionnel vs Mixte : Quel Format Choisir ?
                        </h1>
                        <p className="text-xl md:text-2xl text-white max-w-3xl mx-auto leading-relaxed font-medium">
                            Découvrez quel format de CV correspond le mieux à votre profil et maximisez vos chances de décrocher un entretien
                        </p>
                    </div>
                </div>
            </div>

            <GuideLead slug="format-cv" />

            <article className="px-6 py-16 mx-auto max-w-5xl">
                <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">Les 3 formats de CV</h2>
                    <p className="text-gray-700 leading-relaxed mb-6">
                        Il existe trois formats principaux de CV, chacun avec ses avantages et inconvénients. Le choix du format dépend de votre profil, 
                        de votre parcours professionnel et du type de poste visé.
                    </p>
                </div>

                <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 mb-8">
                    <div className="flex items-center mb-6">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mr-4">
                            <span className="text-2xl">📅</span>
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900">1. CV Chronologique</h2>
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                        Le CV chronologique présente vos expériences professionnelles dans l'ordre chronologique, généralement du plus récent au plus ancien (ordre antéchronologique).
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        <div className="bg-green-50 rounded-2xl p-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-3">✅ Avantages</h3>
                            <ul className="space-y-2 text-gray-700">
                                <li>• Facile à lire et comprendre</li>
                                <li>• Montre l'évolution de carrière</li>
                                <li>• Compatible avec les ATS</li>
                                <li>• Apprécié des recruteurs</li>
                                <li>• Idéal pour carrière linéaire</li>
                            </ul>
                        </div>
                        <div className="bg-red-50 rounded-2xl p-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-3">❌ Inconvénients</h3>
                            <ul className="space-y-2 text-gray-700">
                                <li>• Met en avant les écarts</li>
                                <li>• Moins adapté aux reconversions</li>
                                <li>• Répétitions possibles</li>
                                <li>• Peut révéler l'âge</li>
                            </ul>
                        </div>
                    </div>
                    <div className="bg-blue-50 rounded-2xl p-6 mt-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-3">👤 Idéal pour :</h3>
                        <p className="text-gray-700">Carrière linéaire, progression dans un même secteur, jeunes diplômés avec stages, professionnels expérimentés avec parcours cohérent</p>
                    </div>
                </div>

                <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 mb-8">
                    <div className="flex items-center mb-6">
                        <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mr-4">
                            <span className="text-2xl">🎯</span>
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900">2. CV Fonctionnel</h2>
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                        Le CV fonctionnel met l'accent sur les compétences et réalisations plutôt que sur l'historique chronologique des emplois.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        <div className="bg-green-50 rounded-2xl p-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-3">✅ Avantages</h3>
                            <ul className="space-y-2 text-gray-700">
                                <li>• Masque les écarts de carrière</li>
                                <li>• Met en avant les compétences</li>
                                <li>• Idéal pour reconversion</li>
                                <li>• Focus sur les réalisations</li>
                                <li>• Adapté aux carrières non-linéaires</li>
                            </ul>
                        </div>
                        <div className="bg-red-50 rounded-2xl p-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-3">❌ Inconvénients</h3>
                            <ul className="space-y-2 text-gray-700">
                                <li>• Moins apprécié des recruteurs</li>
                                <li>• Peut sembler suspect</li>
                                <li>• Moins compatible ATS</li>
                                <li>• Difficile à vérifier</li>
                            </ul>
                        </div>
                    </div>
                    <div className="bg-purple-50 rounded-2xl p-6 mt-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-3">👤 Idéal pour :</h3>
                        <p className="text-gray-700">Reconversion professionnelle, écarts de carrière, carrière non-linéaire, freelances, changements fréquents d'emploi</p>
                    </div>
                </div>

                <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 mb-8">
                    <div className="flex items-center mb-6">
                        <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-pink-600 rounded-2xl flex items-center justify-center mr-4">
                            <span className="text-2xl">🔄</span>
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900">3. CV Mixte (Hybride)</h2>
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                        Le CV mixte combine les avantages des deux formats : une section compétences en haut, suivie d'un historique chronologique des expériences.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        <div className="bg-green-50 rounded-2xl p-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-3">✅ Avantages</h3>
                            <ul className="space-y-2 text-gray-700">
                                <li>• Meilleur des deux mondes</li>
                                <li>• Compétences mises en avant</li>
                                <li>• Historique visible</li>
                                <li>• Compatible ATS</li>
                                <li>• Très apprécié des recruteurs</li>
                            </ul>
                        </div>
                        <div className="bg-yellow-50 rounded-2xl p-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-3">⚠️ Points d'attention</h3>
                            <ul className="space-y-2 text-gray-700">
                                <li>• Peut être plus long</li>
                                <li>• Nécessite une bonne organisation</li>
                                <li>• Risque de répétitions</li>
                            </ul>
                        </div>
                    </div>
                    <div className="bg-pink-50 rounded-2xl p-6 mt-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-3">👤 Idéal pour :</h3>
                        <p className="text-gray-700">La plupart des profils, professionnels expérimentés, changements de secteur, carrières avec écarts mais aussi expériences solides</p>
                    </div>
                </div>

                <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">📊 Tableau comparatif</h2>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                                    <th className="border p-4 text-left">Critère</th>
                                    <th className="border p-4 text-center">Chronologique</th>
                                    <th className="border p-4 text-center">Fonctionnel</th>
                                    <th className="border p-4 text-center">Mixte</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="border p-4 font-semibold">Compatibilité ATS</td>
                                    <td className="border p-4 text-center">⭐⭐⭐⭐⭐</td>
                                    <td className="border p-4 text-center">⭐⭐⭐</td>
                                    <td className="border p-4 text-center">⭐⭐⭐⭐⭐</td>
                                </tr>
                                <tr className="bg-gray-50">
                                    <td className="border p-4 font-semibold">Appréciation recruteurs</td>
                                    <td className="border p-4 text-center">⭐⭐⭐⭐⭐</td>
                                    <td className="border p-4 text-center">⭐⭐</td>
                                    <td className="border p-4 text-center">⭐⭐⭐⭐⭐</td>
                                </tr>
                                <tr>
                                    <td className="border p-4 font-semibold">Masque les écarts</td>
                                    <td className="border p-4 text-center">⭐</td>
                                    <td className="border p-4 text-center">⭐⭐⭐⭐⭐</td>
                                    <td className="border p-4 text-center">⭐⭐⭐⭐</td>
                                </tr>
                                <tr className="bg-gray-50">
                                    <td className="border p-4 font-semibold">Mise en avant compétences</td>
                                    <td className="border p-4 text-center">⭐⭐⭐</td>
                                    <td className="border p-4 text-center">⭐⭐⭐⭐⭐</td>
                                    <td className="border p-4 text-center">⭐⭐⭐⭐⭐</td>
                                </tr>
                                <tr>
                                    <td className="border p-4 font-semibold">Facilité de lecture</td>
                                    <td className="border p-4 text-center">⭐⭐⭐⭐⭐</td>
                                    <td className="border p-4 text-center">⭐⭐⭐</td>
                                    <td className="border p-4 text-center">⭐⭐⭐⭐</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl p-8 text-center text-white mb-16">
                    <h2 className="text-3xl font-bold mb-4">🚀 Besoin d'aide pour choisir ?</h2>
                    <p className="text-xl mb-6">Analysez votre CV et obtenez des recommandations personnalisées sur le format le plus adapté</p>
                    <Link to="/" className="inline-block bg-white text-purple-600 font-bold px-8 py-4 rounded-2xl hover:bg-gray-100 transition-colors">
                        Analyser mon CV gratuitement
                    </Link>
                </div>
            </article>
        </div>
    );
};

export default GuideFormatCV;








